import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Card,
  Alert,
} from "antd";
import {
  DeleteOutlined,
  PlusOutlined,
  UsergroupAddOutlined,
} from "@ant-design/icons";
import "./Users.css";
import usePlanStore from "../../store/usePlanStore";
import UserTableSkeleton from "../../components/Skeleton/UserTableSkeleton";
import Footer from "../../components/Footer/Footer";
import { Link } from "react-router-dom";

/* Drawn here rather than pulled from an icon pack, in one stroke weight, so
   the three marks read as a set. The reference mock used Material Symbols,
   which is a whole webfont fetched for three glyphs. */
function MembersMark() {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false">
      <circle cx="7.5" cy="7" r="3" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M2.5 16c0-2.5 2.2-4.2 5-4.2s5 1.7 5 4.2" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M13.5 4.6a3 3 0 0 1 0 4.8M14.6 11.9c2 .5 3.4 2 3.4 4.1" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function SeatsMark() {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false">
      <rect x="2.5" y="3.5" width="15" height="13" rx="2" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M2.5 8h15M7.5 8v8.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

function AvailableMark() {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false">
      <rect x="2.5" y="3.5" width="15" height="13" rx="2" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="m6.5 10.2 2.4 2.4 4.6-4.9" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const Users = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  const [form] = Form.useForm();
  const token = localStorage.getItem("userToken");

  const { planData, planLoading, fetchUserPlan, planError } = usePlanStore();

  useEffect(() => {
    if (!planData && !planLoading) {
      fetchUserPlan();
    }
  }, [planData]);

  const handleAddUser = async (values) => {
    const usersCount = planData?.userIds?.length || 0;
    const limit = planData?.numOfPeople;

    if (!planData || usersCount >= limit) {
      Modal.error({
        title: "Error",
        content: "User limit reached for this plan.",
      });
      return;
    }

    const email = (values.email || "").trim();

    setAddingUser(true);
    try {
      await axios.post(
        // encodeURIComponent is required: the address goes in a query string,
        // and a raw "+" (user+tag@example.com) is decoded server-side as a
        // space, so those addresses were being sent wrong.
        `${process.env.REACT_APP_API_BASE_URL}/auth/v1/addUser?email=${encodeURIComponent(
          email
        )}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      Modal.success({ title: "Success", content: `${email} was added` });
      setModalVisible(false);
      form.resetFields();
      fetchUserPlan();
    } catch (err) {
      console.error("Failed to add user:", err);
      Modal.error({
        title: "Could not add user",
        // Surface what the backend actually said (already registered, not
        // found, and so on) instead of a generic retry message.
        content:
          err.response?.data?.message ||
          err.response?.data?.msg ||
          "Something went wrong. Please try again.",
      });
    } finally {
      setAddingUser(false);
    }
  };

  const handleRemove = async (email) => {
    Modal.confirm({
      title: "Remove teammate",
      content: `Remove ${email} from your team? They will lose access to this plan.`,
      okText: "Remove",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await axios.post(
            `${process.env.REACT_APP_API_BASE_URL}/auth/v1/removeUser?email=${email}`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          Modal.success({
            title: "Success",
            content: "Teammate removed",
          });
          fetchUserPlan();
        } catch (err) {
          console.error("Failed to remove user:", err);
          Modal.error({
            title: "Error",
            content: "Failed to remove teammate. Please try again.",
          });
        }
      },
    });
  };

  const closeAddUser = () => {
    setModalVisible(false);
    form.resetFields();
  };

  // The API returns only _id and email per user, so role is derived rather
  // than invented: the account this plan belongs to is the Admin, everyone
  // added to it is a Member.
  const isPlanOwner = (record) => record._id === planData?.userId;

  const columns = [
    {
      title: "Member",
      dataIndex: "email",
      key: "email",
      align: "left",
      render: (email) => (
        <span className="team-member">
          {/* The initial is derived from the address. The API returns only
              _id and email per user, so there is no name or avatar to show
              and nothing here is invented. */}
          <span className="team-avatar" aria-hidden="true">
            {(email || "?").trim().charAt(0).toUpperCase()}
          </span>
          <span className="team-member-email">{email}</span>
        </span>
      ),
    },
    {
      title: "Role",
      key: "role",
      align: "left",
      width: 140,
      render: (_, record) => (
        <span
          className={`role-badge ${
            isPlanOwner(record) ? "role-badge-admin" : "role-badge-member"
          }`}
        >
          {isPlanOwner(record) ? "Admin" : "Member"}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "left",
      width: 160,
      render: (_, record) => {
        if (isPlanOwner(record)) {
          return <span className="team-self-label">Current user</span>;
        }

        return (
          <Button
            type="text"
            danger
            className="team-remove-btn"
            icon={<DeleteOutlined />}
            onClick={() => handleRemove(record.email)}
          >
            Remove
          </Button>
        );
      },
    },
  ];

  if (planLoading) {
    return <UserTableSkeleton />;
  }

  if (planError) {
    return (
      <div style={{ padding: "20px" }}>
        <Alert message="Error" description={planError} type="error" showIcon />
      </div>
    );
  }

  /* Distinct from planError: the request succeeded, there is just no plan.
     Team seats come with a subscription, so the honest screen here is the
     way to get one, not an empty table with a dead "Add user" button. */
  if (!planData) {
    return (
      /* Footer sits OUTSIDE .users-page, which is a flex row that centres
         its child - as a sibling of .users-container inside it, the footer
         became a second column beside the content instead of a floor under
         it. */
      <>
        <div className="users-page">
          <div className="users-container">
          <header className="page-head">
            <div className="page-head-text">
              <h1 className="page-title">My Team</h1>
              <p className="page-sub">
                People who can sign in and use this plan.
              </p>
            </div>
          </header>

          <div className="team-no-plan">
            <p className="team-no-plan-title">No plan on this account</p>
            <p className="team-no-plan-text">
              Team seats come with a subscription. Pick a plan and you can
              invite people here straight away.
            </p>
            <Link to="/plans">
              <Button type="primary" className="team-add-btn">
                View plans
              </Button>
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const users = planData?.userIds || [];
  const userLimit = planData?.numOfPeople;
  const isUserLimitReached = users.length >= userLimit;

  const seatsLeft =
    typeof userLimit === "number" ? Math.max(userLimit - users.length, 0) : null;

  return (
    <>
      <div className="users-page">
        <div className="users-container">
        <header className="page-head">
          <div className="page-head-text">
            <h1 className="page-title">My Team</h1>
            <p className="page-sub">
              Manage the people who can sign in and use this plan.
            </p>
          </div>
          <div className="page-head-actions">
            <Button
              type="primary"
              className="team-add-btn"
              icon={<PlusOutlined />}
              onClick={() => setModalVisible(true)}
              disabled={isUserLimitReached}
            >
              Add user
            </Button>
          </div>
        </header>

        {/* Counts are derived from the plan payload, not invented. */}
        <div className="stat-row">
          <div className="stat-tile">
            <div className="stat-head">
              <span className="stat-label">Total members</span>
              <span className="stat-icon">
                <MembersMark />
              </span>
            </div>
            <div>
              <span className="stat-value stat-value-accent">
                {users.length}
              </span>
              <span className="stat-caption">
                {users.length === 1
                  ? "Person can sign in on this plan"
                  : "People can sign in on this plan"}
              </span>
            </div>
          </div>

          <div className="stat-tile">
            <div className="stat-head">
              <span className="stat-label">Plan capacity</span>
              <span className="stat-icon">
                <SeatsMark />
              </span>
            </div>
            <div>
              <span className="stat-value">
                {typeof userLimit === "number" ? userLimit : "-"}
              </span>
              <span className="stat-caption">Total seats in current plan</span>
            </div>
          </div>

          <div className="stat-tile">
            <div className="stat-head">
              <span className="stat-label">Available seats</span>
              <span className="stat-icon">
                <AvailableMark />
              </span>
            </div>
            <div>
              <span
                className={`stat-value${
                  seatsLeft === 0 ? " stat-value-muted" : ""
                }`}
              >
                {seatsLeft === null ? "-" : seatsLeft}
              </span>
              <span className="stat-caption">
                {seatsLeft === 0
                  ? "Upgrade to add more people"
                  : "Ready for new team members"}
              </span>
            </div>
          </div>
        </div>

        <Card>
          {isUserLimitReached && (
            <Alert
              message="User limit reached"
              description={
                <span>
                  {`Your current plan allows for a maximum of ${userLimit} users. `}
                  <Link to="/plans">Upgrade Now</Link>
                  {` to add more users`}
                </span>
              }
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          <Table
            columns={columns}
            dataSource={users}
            rowKey="_id"
            pagination={false}
            bordered
            /* Below ~480px the table has no room for three columns at their
               natural width, and without an explicit scroll width antd lets
               the Email column collapse until word-break:break-word forces
               every character onto its own line. scroll={{x}} instead gives
               the table a horizontally-scrollable container of its own, so
               the page never overflows but the row stays one legible line. */
            scroll={{ x: 480 }}
            rowClassName="team-row"
            locale={{
              emptyText: (
                <div className="team-empty">
                  <UsergroupAddOutlined className="team-empty-icon" />
                  <p className="team-empty-title">No teammates yet</p>
                  <p className="team-empty-text">
                    Add a teammate to give them access to this plan.
                  </p>
                </div>
              ),
            }}
          />
        </Card>

        <Modal
          title="Add a teammate"
          open={modalVisible}
          centered
          width={460}
          className="team-modal"
          onCancel={closeAddUser}
          /* Actions live in the modal footer, not inside the form body,
             so this matches the product modal and the buttons sit on their
             own row instead of crowding the input above them. */
          footer={
            <div className="team-modal-actions">
              <Button onClick={closeAddUser} disabled={addingUser}>
                Cancel
              </Button>
              <Button
                type="primary"
                className="team-add-btn"
                loading={addingUser}
                onClick={() => form.submit()}
              >
                Send invite
              </Button>
            </div>
          }
        >
          <Form form={form} onFinish={handleAddUser} layout="vertical">
            <Form.Item
              name="email"
              label="Email address"
              rules={[
                {
                  required: true,
                  type: "email",
                  message: "Enter a valid email address",
                },
              ]}
            >
              <Input placeholder="name@company.com" autoFocus />
            </Form.Item>
            {/* Seat context at the point of decision, so the limit is not a
                surprise only discovered on submit. */}
            {typeof userLimit === "number" && (
              <p className="team-modal-hint">
                {users.length} of {userLimit} seats used on your plan.
              </p>
            )}
          </Form>
        </Modal>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Users;
